import { useEffect, useState } from "react";
import { useApiClient } from "./useApiClient";
import { apiRoutes } from "../api/routes";

/**
 * Polls the count of in-flight trip-optimization batch jobs (a.k.a. "scheduled runs"
 * in the old RoR application's navbar). The old app polled every ~8 seconds via
 * `batch_job/trip_optimization_status` and displayed the number of entries in the
 * `statuses` array alongside a running-person icon in the header.
 *
 * We mirror that here: poll the same endpoint every 60 seconds (matching the
 * cadence of `useNotificationCount` so the badges refresh together), read
 * `response.statuses.length`, and return it. On any failure we silently fall
 * back to 0 — the old app did the same.
 */
export const useScheduledRunsCount = () => {
  const [scheduledRunsCount, setScheduledRunsCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const apiClient = useApiClient();

  useEffect(() => {
    let isMounted = true;

    const fetchScheduledRunsCount = async () => {
      try {
        const response = await apiClient.get(
          apiRoutes.proxy("batch_job/trip_optimization_status")
        );
        if (!isMounted) return;
        const statuses = (response.data?.statuses ?? []) as unknown[];
        setScheduledRunsCount(Array.isArray(statuses) ? statuses.length : 0);
        setError(null);
      } catch (err: unknown) {
        if (!isMounted) return;
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("Failed to fetch scheduled runs count");
        }
        // Fail silently — keep the previous count so a transient network error
        // doesn't flash "0" in the header.
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchScheduledRunsCount();
    const interval = setInterval(fetchScheduledRunsCount, 60000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [apiClient]);

  return { scheduledRunsCount, isLoading, error };
};
